import React, { useEffect, useRef, useState } from 'react';
import { homeownerPost } from '../utils/homeownerApi';
import { shareLink, shareWasCancelled } from '../utils/nativeShare';
import { openExternalUrl } from '../utils/openExternal';
import { followPolicyLink } from '../utils/legal';
import { isNativeApp } from '../utils/platform';
import HomeHistoryReportBody from './HomeHistoryReportBody';
import { HomeHistoryRequestCard } from './HomeHistoryRequestNotice';
import HomeownerPaywall, { NativePlanMessage } from './HomeownerPaywall';
import HomeTransferSeller from './HomeTransferSeller';
import { HOME_REPORT_PLAN_MESSAGE, HOME_REPORT_SUBSCRIBE_LEAD } from '../shared/homeownerEntitlement';
import {
  PRODUCT_NAME,
  TOGGLE_HELP,
  MAINTENANCE_HELP,
  DOCUMENT_HELP,
  INSURANCE_FORM_WARNING,
  FLOOD_NOTE_WARNING,
  DOWNLOAD_NOTE,
  SHARE_CONSENT_BODY,
  SHARE_CONSENT_CHECKBOX,
  CREATE_BUTTON,
  SHARE_DAY_CHOICES,
  DEFAULT_SHARE_DAYS,
  availableSections,
  defaultToggles,
  omission,
} from '../shared/homeHistoryReport';

const ENDPOINT = '/.netlify/functions/home-history-report';

function overflowScrolls(value) {
  return value === 'auto' || value === 'scroll';
}

function scrollerCanScroll(scroller) {
  if (!scroller || !(scroller.scrollHeight > scroller.clientHeight)) return false;
  if (typeof window === 'undefined' || typeof window.getComputedStyle !== 'function') return false;
  const style = window.getComputedStyle(scroller);
  if (!style) return false;
  return overflowScrolls(style.overflowY) || overflowScrolls(style.overflow);
}

function scrollNodeIntoView(node) {
  if (!node) return;
  const scroller = typeof node.closest === 'function' ? node.closest('.app-scroll') : null;
  if (
    scrollerCanScroll(scroller)
    && typeof scroller.scrollTo === 'function'
    && node.getBoundingClientRect
    && scroller.getBoundingClientRect
  ) {
    const top = node.getBoundingClientRect().top - scroller.getBoundingClientRect().top + scroller.scrollTop - 12;
    scroller.scrollTo({ top: top < 0 ? 0 : top, behavior: 'smooth' });
    return;
  }
  if (node.getBoundingClientRect && typeof window !== 'undefined' && typeof window.scrollTo === 'function') {
    const pageTop = window.pageYOffset || window.scrollY || 0;
    const top = node.getBoundingClientRect().top + pageTop - 12;
    window.scrollTo({ top: top < 0 ? 0 : top, behavior: 'smooth' });
    return;
  }
  if (typeof node.scrollIntoView === 'function') {
    node.scrollIntoView({ behavior: 'smooth', block: 'start' });
  }
}

function requestCardVisible(request) {
  if (!request || !request.message) return false;
  if (request.showCard === false) return false;
  if (request.status && request.status !== 'requested') return false;
  if (request.dismissedAt) return false;
  return true;
}

async function postReport(body) {
  if (typeof homeownerPost !== 'function') {
    throw new Error('The report could not be loaded.');
  }
  return homeownerPost(ENDPOINT, body);
}

function shareOpen(share) {
  if (!share || share.revokedAt) return false;
  const expires = new Date(share.expiresAt).getTime();
  return !Number.isNaN(expires) && expires > Date.now();
}

export default function HomeHistoryReportPage({ colors, isMobile, archived, native }) {
  const text = (colors && colors.text) || '#1a1a1a';
  const muted = (colors && colors.textSecondary) || '#666';
  const surface = (colors && colors.surface) || '#fff';
  const border = (colors && colors.border) || '#e6e6e6';
  const [florida, setFlorida] = useState(false);
  const [toggles, setToggles] = useState(() => defaultToggles(false));
  const [documents, setDocuments] = useState([]);
  const [photos, setPhotos] = useState([]);
  const [linkTargets, setLinkTargets] = useState([]);
  const [floodWarning, setFloodWarning] = useState(false);
  const [insuranceWarning, setInsuranceWarning] = useState(false);
  const [reports, setReports] = useState([]);
  const [request, setRequest] = useState(null);
  const [preview, setPreview] = useState(null);
  const [accepted, setAccepted] = useState(false);
  const [days, setDays] = useState(DEFAULT_SHARE_DAYS);
  const [shareWithAgent, setShareWithAgent] = useState(true);
  const [busy, setBusy] = useState('');
  const [notice, setNotice] = useState('');
  const [loaded, setLoaded] = useState(false);
  const [entitled, setEntitled] = useState(null);
  const [transferEnabled, setTransferEnabled] = useState(false);
  const [requestBusy, setRequestBusy] = useState('');
  const previewRef = useRef(null);
  const onDevice = native === true || (native !== false && isNativeApp());

  useEffect(() => {
    if (!preview) return undefined;
    const node = previewRef.current;
    scrollNodeIntoView(node);
    if (!node || typeof ResizeObserver !== 'function') return undefined;
    // The first scroll is clamped while the page is still the short loading
    // placeholder. Scroll again once the paper has its real height.
    const observer = new ResizeObserver(() => {
      scrollNodeIntoView(node);
    });
    observer.observe(node);
    return () => observer.disconnect();
  }, [preview]);

  useEffect(() => {
    let cancelled = false;
    postReport({ action: 'bootstrap' })
      .then((data) => {
        if (cancelled || !data) return;
        setFlorida(Boolean(data.florida));
        setToggles(data.toggles || defaultToggles(Boolean(data.florida)));
        setDocuments(data.documents || []);
        setPhotos(data.photos || []);
        setLinkTargets(data.linkTargets || []);
        setFloodWarning(Boolean(data.floodWarning));
        setInsuranceWarning(Boolean(data.insuranceFormWarning));
        setReports(data.reports || []);
        setRequest(data.request || null);
        setEntitled(data.entitled !== false);
        setTransferEnabled(data.transferEnabled === true);
        setLoaded(true);
      })
      .catch((error) => {
        if (cancelled) return;
        setNotice((error && error.message) || 'The report could not be loaded.');
        setLoaded(true);
      });
    return () => {
      cancelled = true;
    };
  }, []);

  const sections = availableSections(florida);
  const leftOut = omission(toggles, florida);
  const card = {
    background: surface,
    border: `1px solid ${border}`,
    borderRadius: '0.75rem',
    padding: '1rem',
    marginBottom: '1rem',
  };
  const buttonStyle = (disabled) => ({
    background: disabled ? '#c8c8c8' : '#59c4c4',
    color: '#fff',
    border: 'none',
    borderRadius: '0.5rem',
    minHeight: '44px',
    padding: '0.7rem 1rem',
    fontWeight: 650,
    cursor: disabled ? 'default' : 'pointer',
  });

  function patchToggles(next) {
    setToggles(next);
    setPreview(null);
    setAccepted(false);
  }

  function toggleSection(id, on) {
    const sectionsNext = Object.assign({}, toggles.sections);
    sectionsNext[id] = on;
    patchToggles(Object.assign({}, toggles, { sections: sectionsNext }));
  }

  function toggleFlag(key, on) {
    const next = Object.assign({}, toggles);
    next[key] = on;
    patchToggles(next);
  }

  function toggleList(key, id, on) {
    const list = (toggles[key] || []).filter((item) => item !== id);
    if (on) list.push(id);
    const next = Object.assign({}, toggles);
    next[key] = list;
    if (key === 'documentIds' && !on) {
      next.links = (toggles.links || []).filter((link) => link.documentId !== id);
    }
    patchToggles(next);
  }

  function setDocumentLink(documentId, systemId) {
    const links = (toggles.links || []).filter((link) => link.documentId !== documentId);
    if (systemId) links.push({ documentId, systemId });
    patchToggles(Object.assign({}, toggles, { links }));
  }

  async function refresh() {
    const data = await postReport({ action: 'bootstrap' });
    setReports((data && data.reports) || []);
    setRequest((data && data.request) || null);
    if (data) {
      setEntitled(data.entitled !== false);
      setTransferEnabled(data.transferEnabled === true);
    }
  }

  async function runPreview() {
    setBusy('preview');
    setNotice('');
    try {
      const data = await postReport({ action: 'preview', toggles });
      setPreview(data && data.snapshot);
      if (data && data.floodWarning) setFloodWarning(true);
    } catch (error) {
      setNotice((error && error.message) || 'The preview could not be built.');
    } finally {
      setBusy('');
    }
  }

  async function respondToRequest(kind) {
    if (!request || !request.id || requestBusy) return;
    setRequestBusy(kind);
    setNotice('');
    try {
      await postReport({
        action: kind === 'decline' ? 'decline-request' : 'dismiss-request',
        requestId: request.id,
      });
      setRequest(null);
    } catch (error) {
      setNotice((error && error.message) || 'The request could not be updated.');
    } finally {
      setRequestBusy('');
    }
  }

  async function createReport() {
    if (!accepted || archived) return;
    setBusy('create');
    setNotice('');
    try {
      const data = await postReport({ action: 'create', toggles, accepted: true });
      const warnings = (data && data.warnings) || [];
      setNotice(warnings.length ? warnings.join(' ') : `${(data.report && data.report.reportNumber) || 'Report'} saved.`);
      setPreview(data && data.snapshot);
      setAccepted(false);
      await refresh();
    } catch (error) {
      setNotice((error && error.message) || 'The report could not be saved.');
    } finally {
      setBusy('');
    }
  }

  async function createShare(reportId) {
    setBusy('share');
    setNotice('');
    try {
      const allowAgent = Boolean(request && request.canShareWithAgent && shareWithAgent);
      const data = await postReport({
        action: 'share',
        reportId: reportId,
        days: days,
        shareWithAgent: allowAgent,
      });
      const url = data && data.share && data.share.url;
      const sharedNote = data && data.sharedWithAgent ? ' Shared with your sponsor.' : '';
      if (url) {
        try {
          const result = await shareLink(url, PRODUCT_NAME);
          setNotice((result === 'copied' ? 'Link copied.' : 'Link ready.') + sharedNote);
        } catch (error) {
          if (!shareWasCancelled(error)) setNotice(url + sharedNote);
        }
      }
      await refresh();
    } catch (error) {
      setNotice((error && error.message) || 'The link could not be created.');
    } finally {
      setBusy('');
    }
  }

  async function revokeShare(shareId) {
    setBusy('revoke');
    setNotice('');
    try {
      await postReport({ action: 'revoke', shareId });
      setNotice('Link revoked. A downloaded copy cannot be revoked.');
      await refresh();
    } catch (error) {
      setNotice((error && error.message) || 'The link could not be revoked.');
    } finally {
      setBusy('');
    }
  }

  async function downloadPdf(reportId) {
    setBusy('pdf');
    setNotice('');
    try {
      const data = await postReport({ action: 'pdf', reportId });
      if (data && data.url) await openExternalUrl(data.url);
      setNotice(DOWNLOAD_NOTE);
    } catch (error) {
      setNotice((error && error.message) || 'The PDF could not be opened.');
    } finally {
      setBusy('');
    }
  }

  return (
    <div data-testid="home-history-report-page" style={{ color: text, minWidth: 0, maxWidth: '100%' }}>
      <h2 style={{
        fontSize: isMobile ? '1.3rem' : '1.5rem',
        marginTop: 0,
        marginBottom: '0.35rem',
        fontWeight: 300,
        letterSpacing: '0.04em',
        color: text,
      }}
      >
        {PRODUCT_NAME}
      </h2>
      {requestCardVisible(request) ? (
        <HomeHistoryRequestCard
          colors={colors}
          message={request.message}
          busy={requestBusy}
          showCreate={entitled !== false && !archived}
          onCreate={() => scrollNodeIntoView(document.getElementById('home-history-create'))}
          onDecline={() => respondToRequest('decline')}
          onDismiss={() => respondToRequest('dismiss')}
        />
      ) : null}
      {archived ? (
        <p style={card}>This home is archived and can only be viewed.</p>
      ) : null}
      {entitled === false ? (
        <div data-testid="home-history-plan" style={card}>
          <p style={{ fontWeight: 650, marginTop: 0 }}>{HOME_REPORT_PLAN_MESSAGE}</p>
          {onDevice ? (
            <NativePlanMessage colors={{ text: text }} />
          ) : (
            <HomeownerPaywall
              native={false}
              plan={{ reason: 'unpaid', entitled: false }}
              subscribeLead={HOME_REPORT_SUBSCRIBE_LEAD}
            />
          )}
        </div>
      ) : (
        <p style={{ color: muted }}>{TOGGLE_HELP}</p>
      )}
      {notice ? <p role="status">{notice}</p> : null}

      {loaded && entitled !== false && (
        <div style={card}>
          {sections.map((section) => (
            <label key={section.id} style={{ display: 'flex', gap: '0.6rem', alignItems: 'flex-start', marginBottom: '0.55rem' }}>
              <input
                type="checkbox"
                checked={Boolean(toggles.sections && toggles.sections[section.id])}
                disabled={Boolean(archived)}
                onChange={(event) => toggleSection(section.id, event.target.checked)}
              />
              <span>{section.name}</span>
            </label>
          ))}
          <label style={{ display: 'flex', gap: '0.6rem', marginBottom: '0.55rem' }}>
            <input type="checkbox" checked={Boolean(toggles.ownerName)} disabled={Boolean(archived)} onChange={(event) => toggleFlag('ownerName', event.target.checked)} />
            <span>Owner name</span>
          </label>
          <label style={{ display: 'flex', gap: '0.6rem', marginBottom: '0.55rem' }}>
            <input type="checkbox" checked={Boolean(toggles.streetAddress)} disabled={Boolean(archived)} onChange={(event) => toggleFlag('streetAddress', event.target.checked)} />
            <span>Street address</span>
          </label>
          <label style={{ display: 'flex', gap: '0.6rem', marginBottom: '0.55rem' }}>
            <input type="checkbox" checked={Boolean(toggles.taskNotes)} disabled={Boolean(archived)} onChange={(event) => toggleFlag('taskNotes', event.target.checked)} />
            <span>Task notes</span>
          </label>
          {toggles.sections && toggles.sections.maintenance ? <p style={{ color: muted }}>{MAINTENANCE_HELP}</p> : null}
          {floodWarning ? <p style={{ fontWeight: 650 }}>{FLOOD_NOTE_WARNING}</p> : null}
          {toggles.sections && toggles.sections.documents ? (
            <div>
              <p style={{ color: muted }}>{DOCUMENT_HELP}</p>
              {insuranceWarning ? <p style={{ fontWeight: 650 }}>{INSURANCE_FORM_WARNING}</p> : null}
              {documents.map((doc) => {
                const checked = (toggles.documentIds || []).indexOf(doc.id) !== -1;
                const linked = (toggles.links || []).filter((link) => link.documentId === doc.id)[0];
                return (
                  <div key={doc.id} style={{ marginBottom: '0.65rem' }}>
                    <label style={{ display: 'flex', gap: '0.6rem' }}>
                      <input type="checkbox" checked={checked} disabled={Boolean(archived)} onChange={(event) => toggleList('documentIds', doc.id, event.target.checked)} />
                      <span>{doc.title}{doc.category ? ` · ${doc.category}` : ''}</span>
                    </label>
                    {checked && linkTargets.length > 0 ? (
                      <label style={{ display: 'block', margin: '0.35rem 0 0 1.6rem' }}>
                        Attach to a system
                        <select
                          aria-label={`Attach ${doc.title}`}
                          value={linked ? linked.systemId : ''}
                          disabled={Boolean(archived)}
                          onChange={(event) => setDocumentLink(doc.id, event.target.value)}
                          style={{ display: 'block', minHeight: '44px', marginTop: '0.25rem', maxWidth: '100%' }}
                        >
                          <option value="">Do not attach</option>
                          {linkTargets.map((target) => (
                            <option key={target.id} value={target.id}>{target.title}</option>
                          ))}
                        </select>
                      </label>
                    ) : null}
                  </div>
                );
              })}
            </div>
          ) : null}
          {toggles.sections && toggles.sections.photos ? (
            <div>
              {photos.map((photo) => (
                <label key={photo.id} style={{ display: 'flex', gap: '0.6rem', marginBottom: '0.45rem' }}>
                  <input
                    type="checkbox"
                    checked={(toggles.photoIds || []).indexOf(photo.id) !== -1}
                    disabled={Boolean(archived)}
                    onChange={(event) => toggleList('photoIds', photo.id, event.target.checked)}
                  />
                  <span>{photo.label}</span>
                </label>
              ))}
            </div>
          ) : null}
          <p style={{ fontWeight: 650 }}>{leftOut.statement}</p>
          {leftOut.privacyLine ? <p>{leftOut.privacyLine}</p> : null}
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.5rem', marginTop: '0.75rem' }}>
            <button
              type="button"
              aria-busy={busy === 'preview'}
              style={buttonStyle(busy === 'preview')}
              disabled={busy === 'preview'}
              onClick={runPreview}
            >
              {busy === 'preview' ? 'Preparing preview…' : 'Preview'}
            </button>
          </div>
          {!archived && (
            <div id="home-history-create" style={{ marginTop: '1rem' }}>
              <h3 style={{ marginBottom: '0.35rem' }}>Before you share</h3>
              <p>{SHARE_CONSENT_BODY}</p>
              <label style={{ display: 'flex', gap: '0.6rem', alignItems: 'flex-start' }}>
                <input type="checkbox" checked={accepted} onChange={(event) => setAccepted(event.target.checked)} />
                <span>{SHARE_CONSENT_CHECKBOX}</span>
              </label>
              <p>
                <a href="/terms" onClick={followPolicyLink}>Terms</a>
                {' · '}
                <a href="/privacy" onClick={followPolicyLink}>Privacy Policy</a>
              </p>
              <button
                type="button"
                data-testid="home-history-create-submit"
                style={{ ...buttonStyle(!accepted || Boolean(busy)), marginTop: '0.75rem' }}
                disabled={!accepted || Boolean(busy)}
                onClick={createReport}
              >
                {CREATE_BUTTON}
              </button>
            </div>
          )}
        </div>
      )}

      {preview && entitled !== false ? (
        <div ref={previewRef} data-testid="home-history-preview">
          <HomeHistoryReportBody snapshot={preview} />
        </div>
      ) : null}

      {reports.map((report) => (
        <section key={report.id} style={card}>
          <h3 style={{ marginTop: 0 }}>{report.reportNumber}</h3>
          <p style={{ color: muted, marginTop: 0 }}>{report.preparedLabel}</p>
          <button type="button" style={buttonStyle(busy === 'pdf')} onClick={() => downloadPdf(report.id)}>
            Download PDF
          </button>
          <p style={{ color: muted }}>{DOWNLOAD_NOTE}</p>
          {!archived && entitled !== false && (
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.5rem', alignItems: 'center' }}>
              <label>
                Link lifetime
                <select
                  aria-label="Link lifetime"
                  value={days}
                  onChange={(event) => setDays(Number(event.target.value))}
                  style={{ display: 'block', minHeight: '44px' }}
                >
                  {SHARE_DAY_CHOICES.map((choice) => (
                    <option key={choice} value={choice}>{choice} days</option>
                  ))}
                </select>
              </label>
              {request && request.canShareWithAgent ? (
                <label style={{ display: 'flex', gap: '0.6rem', alignItems: 'flex-start' }}>
                  <input
                    type="checkbox"
                    checked={shareWithAgent}
                    onChange={(event) => setShareWithAgent(event.target.checked)}
                  />
                  <span>
                    Share this link with {request.agentName || 'your sponsor'}, who asked for your report.
                  </span>
                </label>
              ) : null}
              <button type="button" style={buttonStyle(Boolean(busy))} disabled={Boolean(busy)} onClick={() => createShare(report.id)}>
                Create share link
              </button>
            </div>
          )}
          {(report.shares || []).map((share) => (
            <div key={share.id} style={{ marginTop: '0.75rem' }}>
              <div>{shareOpen(share) ? 'Open' : 'Closed'} · {share.viewCount || 0} views · {share.lifetimeDays} days</div>
              {share.url ? <div style={{ overflowWrap: 'anywhere' }}>{share.url}</div> : null}
              {shareOpen(share) ? (
                <button type="button" style={{ ...buttonStyle(false), marginTop: '0.4rem' }} onClick={() => revokeShare(share.id)}>
                  Revoke link
                </button>
              ) : null}
            </div>
          ))}
        </section>
      ))}

      {transferEnabled && !archived && entitled !== false && reports.length > 0 ? (
        <HomeTransferSeller
          colors={{ text, textSecondary: muted, surface, border }}
          reports={reports}
        />
      ) : null}
    </div>
  );
}
